import { useEffect, useRef, useState } from 'react';

export default function Features() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: 'ri-calendar-check-line',
      title: 'Automatic Booking Sync',
      description: 'Pulls your bookings from Airbnb, VRBO, and Booking.com instantly, and creates cleaning tasks automatically.',
      color: 'from-teal-500 to-teal-600',
      delay: 'delay-0'
    },
    {
      icon: 'ri-notification-3-line',
      title: 'Instant Cleaner Notifications',
      description: 'Cleaners are auto-assigned for each turnover and alerted immediately with job details — no more missed messages.',
      color: 'from-blue-500 to-blue-600',
      delay: 'delay-100'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Custom Cleaning Checklists',
      description: 'Set your exact cleaning steps once — cleaners follow the same standard every time.',
      color: 'from-purple-500 to-purple-600',
      delay: 'delay-200'
    },
    {
      icon: 'ri-camera-line',
      title: 'Check-In, Checkout & Photos',
      description: 'Track when cleaners arrive, finish, and upload before/after photos for peace of mind.',
      color: 'from-emerald-500 to-emerald-600',
      delay: 'delay-300'
    },
    {
      icon: 'ri-building-line',
      title: 'Property & Multi-Unit Management',
      description: 'Manage all your properties in one dashboard and track turnovers effortlessly — even with multiple cleaners.',
      color: 'from-orange-500 to-orange-600',
      delay: 'delay-[400ms]'
    },
    {
      icon: 'ri-inbox-line',
      title: 'Inventory Management',
      description: 'Inventory tracking made easy — cleaners flag low items so you never miss a restock.',
      color: 'from-pink-500 to-pink-600',
      delay: 'delay-[500ms]'
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-teal-900 mb-4">
            Everything You Need
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Powerful features designed to make vacation rental management effortless
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`transition-all duration-700 transform ${
                isVisible
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-10 opacity-0'
              } ${feature.delay}`}
            >
              <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full border border-gray-100">
                <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-6 shadow-lg`}>
                  <i className={`${feature.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
